import {
  ReactNode,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import styled, { css } from 'styled-components';

import type { TerminalConfig } from './config';
import DimeLogo from './components/DimeLogo';
import {
  TerminalConfigProvider,
  useTerminalConfig,
} from './context/config-context';
import ChatView from './components/chat/ChatView';
import { AgentUrlSetup, SignedOutState } from './components/chat/setup';
import CronView from './components/cron/CronView';
import ExamplesView from './components/examples/ExamplesView';
import RecentsView from './components/recents/RecentsView';
import SkillsView from './components/skills/SkillsView';
import { AgentProvider } from './providers/AgentProvider';
import { useAgentActions, useAgentState } from './providers/contexts';
import {
  TabsProvider,
  useTabsState,
  type TerminalTab,
} from './providers/TabsProvider';

export interface TerminalProps {
  /** Everything host-specific. The component builds all wiring from this. */
  readonly config: TerminalConfig;
  /**
   * Optional effect-only nodes rendered inside the agent context (so they can
   * call `useTerminalAgent()`) but outside the visible layout. Use to drive the
   * agent from host state — e.g. Paradigm's block-trade context pump or a
   * pending-message drain. They should render `null`; anything visible belongs
   * in the host shell around `<Terminal>`, not here.
   */
  readonly agentEffects?: ReactNode;
  /**
   * Compact chrome for space-constrained embeds (e.g. Paradigm's bottom-tab
   * dock). Hides the package's top bar (logo + sidebar toggle) and folds the
   * sidebar toggle into the sidebar itself — the collapse control lives at the
   * top of the sessions list, and a small floating button reopens the sidebar
   * when collapsed. Default `false` (top bar shown).
   */
  readonly compact?: boolean;
}

/**
 * The self-contained Dime Terminal. Mirrors Paradex's inner layout: a left
 * **sessions sidebar** (`RecentsView`) beside a **main pane** that tab-routes
 * chat / examples / cron. The host owns the outer shell only — where this is
 * placed, and its drawer / pin / resize / fullscreen chrome.
 */
export function Terminal({
  config,
  agentEffects,
  compact,
}: TerminalProps): JSX.Element {
  return (
    <TerminalConfigProvider value={config}>
      <AgentProvider>
        {agentEffects}
        <TabsProvider initialTab="chat">
          <AuthGate compact={compact} />
        </TabsProvider>
      </AgentProvider>
    </TerminalConfigProvider>
  );
}

/**
 * Top-level auth gate. The terminal owns its own login: until it's paired the
 * whole pane IS the login screen; once paired, the full layout mounts. Pairing
 * lives in the package's own storage, so this survives host remounts — a
 * disconnect stays disconnected instead of silently reconnecting on remount.
 */
function AuthGate({ compact }: { readonly compact?: boolean }): JSX.Element {
  const { accountId } = useTerminalConfig();
  const { isPaired } = useAgentState();
  if (accountId == null) return <SignedOutState />;
  if (!isPaired) return <AgentUrlSetup accountId={accountId} />;
  return <TerminalLayout compact={compact} />;
}

/** Sessions-sidebar width: user-adjustable via a drag handle, clamped to a
 *  sane range and remembered across reloads. */
const SIDEBAR_DEFAULT_WIDTH = 280;
const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 480;
// Namespaced like the rest of the package's localStorage keys (see
// SkillsView's `dime-terminal:*` keys) so multiple hosts embedding the
// terminal on one origin don't clobber each other's sidebar width.
const SIDEBAR_WIDTH_STORAGE_KEY = 'dime-terminal:sidebar-width';

function clampSidebarWidth(width: number): number {
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, width));
}

function readStoredSidebarWidth(): number {
  if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH;
  try {
    const raw = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    // `Number('')` is `0`, not `NaN` — an empty/blank/corrupted entry would
    // otherwise pass the finite check and clamp to the min width instead of
    // falling back to the default.
    const parsed = raw === null || raw.trim() === '' ? NaN : Number(raw);
    return Number.isFinite(parsed)
      ? clampSidebarWidth(parsed)
      : SIDEBAR_DEFAULT_WIDTH;
  } catch {
    // Sandboxed iframe / storage-blocked / private mode — localStorage
    // access can throw (SecurityError). Fall back to the default rather
    // than crashing the tree on mount.
    return SIDEBAR_DEFAULT_WIDTH;
  }
}

function renderTab(id: TerminalTab): ReactNode {
  switch (id) {
    case 'examples':
      return <ExamplesView />;
    case 'skills':
      return <SkillsView />;
    case 'cron':
      return <CronView />;
    case 'chat':
    default:
      // The "+ New Chat" / "Private Chat" controls live in the sidebar only
      // — closing the sidebar leaves the chat pane uncluttered by chrome.
      return <ChatView />;
  }
}

function useIsBelowMd(): boolean {
  const query = '(max-width: 768px)';
  const supported =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [below, setBelow] = useState(
    () => supported && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (!supported) return undefined;
    const mq = window.matchMedia(query);
    const onChange = () => setBelow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [supported]);
  return below;
}

/** Step (px) an arrow-key press moves the sidebar edge; Shift multiplies it,
 *  matching the common "coarse adjust" convention for draggable separators. */
const SIDEBAR_RESIZE_STEP = 10;
const SIDEBAR_RESIZE_STEP_LARGE = 40;

// CSS custom property carrying the live sidebar width, set on `Body` (the
// common ancestor of Sidebar/SidebarInner/ResizeHandle). During a drag it's
// mutated directly via `bodyRef` — bypassing React state — so a mousemove
// doesn't re-render TerminalLayout's whole subtree (RecentsView's inline
// handlers, `renderTab`) or mint a new styled-components class per pixel
// (per-value classes never get garbage-collected in styled-components v5).
// React state (`width`) is only committed once, at drag end.
const SIDEBAR_WIDTH_VAR = '--sidebar-width';

function useSidebarResize(enabled: boolean) {
  const [width, setWidth] = useState(readStoredSidebarWidth);
  const [resizing, setResizing] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const applyWidth = (w: number) => {
    bodyRef.current?.style.setProperty(SIDEBAR_WIDTH_VAR, `${w}px`);
  };

  // Pointer Events (not mouse-only) so this also works on touch-capable
  // desktop/tablet hybrids, and `setPointerCapture` keeps delivering move/up
  // events to the handle even if the cursor leaves the window mid-drag.
  const onHandlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!enabled || resizing) return;
    e.preventDefault();
    const handle = e.currentTarget;
    const { pointerId } = e;
    handle.setPointerCapture(pointerId);
    const startX = e.clientX;
    const startWidth = width;
    let latestWidth = startWidth;
    setResizing(true);

    const onPointerMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      latestWidth = clampSidebarWidth(
        startWidth + (moveEvent.clientX - startX),
      );
      applyWidth(latestWidth);
    };
    // `pointerup` alone misses every path where the browser aborts the
    // pointer stream instead of ending it normally (touch takeover, OS
    // gesture, alt-tab mid-drag) or the handle unmounts mid-drag (crossing
    // the mobile breakpoint) — `lostpointercapture` fires in all of those,
    // so cleanup lives there instead of duplicating it across every event.
    const onPointerEnd = (endEvent: PointerEvent) => {
      if (endEvent.pointerId !== pointerId) return;
      setResizing(false);
      setWidth(latestWidth);
      handle.removeEventListener('pointermove', onPointerMove);
      handle.removeEventListener('pointerup', onPointerEnd);
      handle.removeEventListener('pointercancel', onPointerEnd);
      handle.removeEventListener('lostpointercapture', onPointerEnd);
    };
    handle.addEventListener('pointermove', onPointerMove);
    handle.addEventListener('pointerup', onPointerEnd);
    handle.addEventListener('pointercancel', onPointerEnd);
    handle.addEventListener('lostpointercapture', onPointerEnd);
  };

  // Keyboard equivalent for the `role="separator"` semantics — a separator
  // that only responds to a mouse/pointer drag is an ARIA lie.
  const onHandleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!enabled) return;
    const step = e.shiftKey ? SIDEBAR_RESIZE_STEP_LARGE : SIDEBAR_RESIZE_STEP;
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        setWidth((w) => clampSidebarWidth(w - step));
        break;
      case 'ArrowRight':
        e.preventDefault();
        setWidth((w) => clampSidebarWidth(w + step));
        break;
      case 'Home':
        e.preventDefault();
        setWidth(SIDEBAR_MIN_WIDTH);
        break;
      case 'End':
        e.preventDefault();
        setWidth(SIDEBAR_MAX_WIDTH);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (resizing) return;
    try {
      window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(width));
    } catch (err) {
      // Storage unavailable (private mode, quota, sandboxed iframe) — width
      // stays in-memory for this session; warn once rather than fail silently.
      // eslint-disable-next-line no-console
      console.warn('[Terminal] could not persist sidebar width', err);
    }
  }, [width, resizing]);

  // Defensive belt-and-suspenders: `enabled` flips false when crossing to
  // mobile, which also unmounts ResizeHandle (see its render-site guard),
  // and removing a pointer-captured node from the DOM fires
  // `lostpointercapture` (already handled above). This just guarantees
  // `resizing` can't stay stuck true even if a browser doesn't fire that
  // event for a detached node.
  useEffect(() => {
    if (!enabled) setResizing(false);
  }, [enabled]);

  return { width, resizing, onHandlePointerDown, onHandleKeyDown, bodyRef };
}

function TerminalLayout({
  compact = false,
}: {
  readonly compact?: boolean;
}): JSX.Element {
  const { features } = useTerminalConfig();
  const { activeTab } = useTabsState();
  const actions = useAgentActions();
  const isBelowMd = useIsBelowMd();
  const sidebarResize = useSidebarResize(!isBelowMd);

  // Sessions sidebar: open on desktop, collapsed on mobile (re-applied when
  // the viewport crosses the breakpoint), with a manual toggle.
  const [sidebarOpen, setSidebarOpen] = useState(!isBelowMd);
  useEffect(() => {
    setSidebarOpen(!isBelowMd);
  }, [isBelowMd]);

  // Main-pane routing. There is no top tab strip — the sidebar drives which
  // view shows (chat / examples / skills / cron). `activeTab` may be a non-main
  // value (e.g. 'recents' from a session switch) or 'cron' while the feature is
  // off; fall back to chat so the pane always has content.
  const activeId: TerminalTab =
    activeTab === 'examples'
      ? 'examples'
      : activeTab === 'skills'
        ? 'skills'
        : activeTab === 'cron' && features?.cron === true
          ? 'cron'
          : 'chat';

  return (
    <Root data-terminal-root>
      {/* Compact mode drops this top bar entirely — the sidebar toggle moves
          into the sidebar (RecentsView) and a floating button (below) reopens
          it when collapsed — so a space-constrained host reclaims the row. */}
      {!compact && (
        <Header>
          <LogoSlot>
            <DimeLogo size={20} />
          </LogoSlot>
          <ToggleButton
            type="button"
            aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            onClick={() => setSidebarOpen((o) => !o)}
          >
            <SidebarToggleIcon />
          </ToggleButton>
        </Header>
      )}
      <Body
        ref={sidebarResize.bodyRef}
        style={
          {
            [SIDEBAR_WIDTH_VAR]: `${sidebarResize.width}px`,
          } as CSSProperties
        }
      >
        <Sidebar
          $open={sidebarOpen}
          $isMobile={isBelowMd}
          $resizing={sidebarResize.resizing}
        >
          <SidebarInner $isMobile={isBelowMd}>
            <RecentsView
              onReconnect={() => actions.connect()}
              onDisconnect={() => actions.disconnect()}
              onToggleSidebar={
                compact ? () => setSidebarOpen((o) => !o) : undefined
              }
            />
          </SidebarInner>
        </Sidebar>
        {/* Rendered as Body's child, not Sidebar's — Sidebar is
            `overflow: hidden` (needed so its fixed-width SidebarInner clips
            during the collapse-width transition), which clipped this handle
            down to a ~3px sliver when it lived inside. Positioned against
            Body (also `position: relative`) at the live sidebar edge instead. */}
        {!isBelowMd && sidebarOpen && (
          <ResizeHandle
            onPointerDown={sidebarResize.onHandlePointerDown}
            onKeyDown={sidebarResize.onHandleKeyDown}
            tabIndex={0}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
            aria-valuenow={sidebarResize.width}
            aria-valuemin={SIDEBAR_MIN_WIDTH}
            aria-valuemax={SIDEBAR_MAX_WIDTH}
          />
        )}
        {/* Compact + collapsed: the only way back to the sidebar, since the top
            bar (and its toggle) is gone. Floats over the main pane's top-left. */}
        {compact && !sidebarOpen && (
          <OpenSidebarButton
            type="button"
            aria-label="Open sidebar"
            title="Open sidebar"
            onClick={() => setSidebarOpen(true)}
          >
            <SidebarToggleIcon />
          </OpenSidebarButton>
        )}
        <MainPane>{renderTab(activeId)}</MainPane>
      </Body>
    </Root>
  );
}

function SidebarToggleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <rect
        x="3"
        y="4"
        width="18"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth="2"
      />
      <line
        x1="9"
        y1="4"
        x2="9"
        y2="20"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  color: var(--text-primary);
  background: var(--bg-color-page);
`;

const Header = styled.div`
  position: relative;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 10px 0 16px;
  border-bottom: 1px solid var(--divider-color);
`;

const LogoSlot = styled.div`
  display: flex;
  align-items: center;
`;

const ToggleButton = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  padding: 4px;
  border-radius: 5px;
  color: var(--text-label);

  &:hover {
    color: var(--text-primary);
    background: var(--hover-bg);
  }
`;

// Compact-mode floating control: shown only when the sidebar is collapsed (the
// top bar that would otherwise hold the toggle is hidden in compact mode).
// Pinned to the main pane's top-left with a solid background so it stays
// legible over chat content.
const OpenSidebarButton = styled.button`
  all: unset;
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 3;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 5px;
  color: var(--text-label);
  background: var(--bg-color-page);

  &:hover {
    color: var(--text-primary);
    background: var(--hover-bg);
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  overflow: hidden;
  position: relative;
`;

const SIDEBAR_TRANSITION = 'width 180ms cubic-bezier(0.16, 1, 0.3, 1)';

const Sidebar = styled.aside<{
  $open: boolean;
  $isMobile: boolean;
  $resizing: boolean;
}>`
  ${({ $isMobile, $open, $resizing }) =>
    $isMobile
      ? css`
          position: absolute;
          inset: 0 auto 0 0;
          z-index: 2;
          width: 100%;
          background: var(--bg-color-page);
          transform: translate3d(${$open ? '0' : '-100%'}, 0, 0);
          transition: transform 240ms cubic-bezier(0.16, 1, 0.3, 1);
          ${$open ? 'box-shadow: 8px 0 28px rgba(0, 0, 0, 0.35);' : ''}
        `
      : css`
          flex-shrink: 0;
          position: relative;
          min-height: 0;
          width: ${$open ? `var(${SIDEBAR_WIDTH_VAR})` : '0'};
          border-right: ${$open ? '1px solid var(--divider-color)' : 'none'};
          overflow: hidden;
          transition: ${$resizing ? 'none' : SIDEBAR_TRANSITION};
        `}
`;

const SidebarInner = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  ${({ $isMobile }) =>
    $isMobile
      ? css`
          width: 100%;
          height: 100%;
        `
      : css`
          position: absolute;
          inset: 0 0 0 auto;
          width: var(${SIDEBAR_WIDTH_VAR});
        `}
`;

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  /* Straddles the sidebar/main-pane border, which sits var(--sidebar-width)
     px from Body's left edge (Body, not Sidebar, is this element's
     containing block — see the render-site comment for why). Reads the same
     CSS var Sidebar/SidebarInner do, so it tracks the live width during a
     drag with zero extra props/re-renders. */
  left: calc(var(${SIDEBAR_WIDTH_VAR}) - 3px);
  width: 6px;
  cursor: col-resize;
  z-index: 1;
  touch-action: none;

  &:hover,
  &:active {
    background: var(--hover-bg);
  }

  &:focus-visible {
    outline: none;
    background: var(--accent-color, var(--hover-bg));
  }
`;

const MainPane = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
